import React, { useState, useEffect } from 'react';
import { Card } from 'react-daisyui';
import { Package, Activity } from 'react-feather';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

const BlockCard = ({ block }) => {
  const timeSince = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return `${seconds} secs ago`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes} min${minutes > 1 ? 's' : ''} ago`;
  };

  return (
    <Card className="bg-gray-900 border border-gray-800 w-48 h-36">
      <Card.Body className="p-4">
        <div className="flex items-center mb-2">
          <Package size={20} className="mr-2 text-cyan-400" />
          <Card.Title tag="h3" className="text-lg">{block.height}</Card.Title>
        </div>
        <p className="text-xs text-gray-400">{timeSince(block.timestamp)}</p>
        <p className="text-xs text-cyan-400 truncate">
          Hash: {block.hash.substring(0, 6)}...{block.hash.substring(block.hash.length - 6)}
        </p>
        <p className="text-xs text-gray-400">{block.tx_count} txns</p>
      </Card.Body>
    </Card>
  );
};

const TPSCard = ({ tps }) => {
  const maxTPS = 250;
  const percentage = Math.min((tps / maxTPS) * 100, 100);

  return (
    <Card className="bg-gray-900 border border-gray-800 w-48 h-36">
      <Card.Body className="p-1">
        <div className="flex items-center justify-between mb-0.5">
          <div className="flex items-center">
            <Activity size={20} className="mr-2 text-cyan-400" />
            <Card.Title tag="h3" className="text-lg">TPS</Card.Title>
          </div>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-28 h-14 mb-0.5">
            <CircularProgressbar
              value={percentage}
              circleRatio={0.5}
              styles={buildStyles({
                rotation: 0.75,
                strokeLinecap: 'butt',
                trailColor: '#1F2937',
                pathColor: '#22D3EE',
              })}
            />
          </div>
          <p className="text-xl font-bold text-cyan-400">{tps.toFixed(2)}</p>
          <p className="text-xs text-gray-400">Transactions Per Second</p>
        </div>
      </Card.Body>
    </Card>
  );
};

const LatestBlocks = () => {
  const [blocks, setBlocks] = useState([]);
  const [tps, setTPS] = useState(0);

  const calculateTPS = (blocks) => {
    // Count blocks with transactions > 0
    const blocksWithTx = blocks.filter(block => block.tx_count > 0).length;
    
    // If no blocks have transactions, return 0
    if (blocksWithTx === 0) return 0;

    // Sum all transactions
    const totalTx = blocks.reduce((sum, block) => sum + block.tx_count, 0);
    
    // Divide by number of blocks that have transactions
    return totalTx / blocksWithTx;
  };

  const fetchBlocks = async () => {
    try {
      const response = await fetch('https://berachain-v2-blacknodes-el-api.blacknodes.net/api/v2/main-page/blocks');
      const data = await response.json();
      
      setBlocks(prevBlocks => {
        if (data[0].height !== prevBlocks[0]?.height) {
          return data;
        }
        return prevBlocks;
      });

      const newTPS = calculateTPS(data);
      setTPS(newTPS);
    } catch (error) {
      console.error('Error fetching blocks:', error);
    }
  };

  useEffect(() => {
    fetchBlocks();
    const interval = setInterval(fetchBlocks, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex space-x-4 overflow-x-auto pb-4">
      {blocks.map((block) => (
        <BlockCard key={block.height} block={block} />
      ))}
      <TPSCard tps={tps} />
    </div>
  );
};

export default LatestBlocks;